export const AUTH_TOKEN_STORAGE_KEY = "auth.token";
export const AUTH_SESSION_EXPIRED_EVENT = "auth:session-expired";

export const getStoredAuthToken = () =>
  localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) ||
  sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY);

export const storeAuthToken = (token, stayLoggedIn) => {
  localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);

  const storage = stayLoggedIn ? localStorage : sessionStorage;
  storage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
};

export const clearStoredAuthToken = () => {
  localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
};

export const getTokenExpirationTime = (token) => {
  try {
    const [, payload] = String(token || "").split(".");
    if (!payload) return null;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decodedPayload = JSON.parse(window.atob(base64));
    const expirationSeconds = Number(decodedPayload.exp);

    return Number.isFinite(expirationSeconds) ? expirationSeconds * 1000 : null;
  } catch {
    return null;
  }
};

export const isTokenExpired = (token, now = Date.now()) => {
  const expirationTime = getTokenExpirationTime(token);
  return expirationTime !== null && expirationTime <= now;
};

export const getUsableStoredAuthToken = () => {
  const token = getStoredAuthToken();
  if (token && isTokenExpired(token)) {
    clearStoredAuthToken();
    return null;
  }
  return token;
};

export const notifyAuthSessionExpired = () => {
  clearStoredAuthToken();
  window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
};

const getHeaderValue = (headers, name) => {
  if (!headers) return "";

  if (headers instanceof Headers) {
    return headers.get(name) || "";
  }

  if (Array.isArray(headers)) {
    const entry = headers.find(
      ([key]) => String(key).toLowerCase() === name.toLowerCase(),
    );
    return entry?.[1] || "";
  }

  return headers[name] || headers[name.toLowerCase()] || "";
};

const hasBearerAuthorization = (input, init) => {
  const requestHeader =
    typeof Request !== "undefined" && input instanceof Request
      ? input.headers.get("Authorization")
      : "";
  const headerValue = getHeaderValue(init?.headers, "Authorization") || requestHeader;

  return /^Bearer\s+\S+/i.test(headerValue);
};

export const installAuthExpiredFetchHandler = () => {
  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return () => {};
  }

  const previousFetch = window.fetch;

  const authAwareFetch = async (input, init) => {
    const response = await previousFetch.call(window, input, init);

    if (response.status === 401 && hasBearerAuthorization(input, init)) {
      notifyAuthSessionExpired();
    }

    return response;
  };

  window.fetch = authAwareFetch;

  return () => {
    if (window.fetch === authAwareFetch) {
      window.fetch = previousFetch;
    }
  };
};
